import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFixtures } from '../hooks/useFantasyData';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import '../styles/FantasyFixtures.css';

const FantasyFixturesPage = ({ setActiveTab }) => {
  const { t } = useTranslation();
  const [season] = useState(2024);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const { fixtures, loading, error } = useFixtures(season);

  const filteredFixtures = statusFilter
    ? fixtures.filter((f) => f.status === statusFilter)
    : fixtures;

  const sortedFixtures = [...filteredFixtures].sort((a, b) => {
    const dateA = new Date(a.fixtureDate || 0);
    const dateB = new Date(b.fixtureDate || 0);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const statusCounts = {
    NOT_STARTED: fixtures.filter((f) => f.status === 'NOT_STARTED').length,
    LIVE: fixtures.filter((f) => f.status === 'LIVE').length,
    FINISHED: fixtures.filter((f) => f.status === 'FINISHED').length
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'NOT_STARTED':
        return 'badge bg-secondary';
      case 'LIVE':
        return 'badge bg-danger';
      case 'FINISHED':
        return 'badge bg-success';
      default:
        return 'badge bg-dark';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error} />;

  return (
    <div className="fantasy-fixtures-page container mt-4">
      <button
        className="btn-back-header mb-3"
        onClick={() => setActiveTab(9)}
        aria-label="Back to Fantasy main"
        title="Back to Fantasy"
      >
        ← Back
      </button>
      <h1 className="mb-4">{t('fantasy.fixtures.title')}</h1>

      <div className="fixtures-stats mb-4">
        <div className="stat-card">
          <span className="stat-label">{t('fantasy.fixtures.not_started')}</span>
          <span className="stat-value">{statusCounts.NOT_STARTED}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('fantasy.fixtures.live')}</span>
          <span className="stat-value">{statusCounts.LIVE}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('fantasy.fixtures.finished')}</span>
          <span className="stat-value">{statusCounts.FINISHED}</span>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="statusFilter">{t('fantasy.fixtures.filter_status')}</label>
            <select
              id="statusFilter"
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t('fantasy.fixtures.all_statuses')}</option>
              <option value="NOT_STARTED">{t('fantasy.fixtures.not_started')}</option>
              <option value="LIVE">{t('fantasy.fixtures.live')}</option>
              <option value="FINISHED">{t('fantasy.fixtures.finished')}</option>
            </select>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="sortOrder">{t('fantasy.fixtures.sort_order')}</label>
            <select
              id="sortOrder"
              className="form-control"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="asc">{t('fantasy.fixtures.oldest_first')}</option>
              <option value="desc">{t('fantasy.fixtures.newest_first')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="fixtures-list">
        {sortedFixtures.map((fixture) => (
          <div key={fixture.id} className="fixture-card">
            <div className="fixture-header">
              <span className={getStatusBadgeClass(fixture.status)}>
                {t(`fantasy.fixtures.status.${fixture.status.toLowerCase()}`)}
              </span>
              <span className="fixture-date">
                {formatDate(fixture.fixtureDate)}
              </span>
            </div>

            <div className="fixture-body">
              <div className="fixture-teams">
                <div className="team home-team">
                  <span className="team-name">{fixture.homeTeam?.name || 'N/A'}</span>
                </div>

                <div className="fixture-score">
                  {fixture.status === 'FINISHED' ? (
                    <>
                      <span className="score-value">{fixture.homeScore || 0}</span>
                      <span className="score-separator">-</span>
                      <span className="score-value">{fixture.awayScore || 0}</span>
                    </>
                  ) : (
                    <span className="score-vs">vs</span>
                  )}
                </div>

                <div className="team away-team">
                  <span className="team-name">{fixture.awayTeam?.name || 'N/A'}</span>
                </div>
              </div>

              <div className="fixture-gameweek">
                {t('fantasy.fixtures.gameweek')}: <strong>{fixture.gameweekNumber}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedFixtures.length === 0 && (
        <div className="alert alert-info mt-4">
          {t('fantasy.fixtures.no_fixtures')}
        </div>
      )}

      <div className="fixtures-total mt-4">
        <p className="text-muted">
          {t('fantasy.fixtures.total')}: <strong>{sortedFixtures.length}</strong>
        </p>
      </div>
    </div>
  );
};

export default FantasyFixturesPage;
